import Constants from 'expo-constants';
import { useCallback, useState } from 'react';

import { PaymentProcessor } from '@fhs-legacy/frontend/src/generated/rbi-graphql';
import logger from '@fhs-legacy/frontend/src/utils/logger';

import type { GooglePayDetails, IGoogleConfig } from '../../../cart/src/payment/payment-types';

interface GooglePayForReloadConfig {
  processor: PaymentProcessor;
  storeId: string;
}

interface PaymentConfig {
  region: 'US' | 'CA';
  currencyCode: string;
  totalAmount: string;
  gateway: string;
  gatewayMerchantId: string;
  merchantName: string;
  merchantId?: string;
  environment: string;
  networks: IGoogleConfig['paymentsNetworks'];
}

/**
 * Hook to trigger Google Pay payment sheet for gift card reload.
 *
 * Mirrors the checkout flow in fresh/cart google-pay-step.web.ts
 * and the legacy workspaces/frontend use-google-pay.web.ts.
 */
export function useGooglePayForReload(config: GooglePayForReloadConfig) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const region: 'US' | 'CA' = Constants?.expoConfig?.extra?.eas?.buildRegion ?? 'US';

  const getConfiguration = useCallback((): IGoogleConfig | null => {
    const googleConfig: IGoogleConfig =
      Constants?.expoConfig?.extra?.eas?.US?.google ||
      Constants?.expoConfig?.extra?.eas?.CA?.google;

    if (!googleConfig) {
      logger.error({
        message: '[GooglePay Reload] Google Pay configuration not found',
      });
      return null;
    }

    return googleConfig;
  }, []);

  const buildPaymentConfig = useCallback(
    (amount: number, googleConfig: IGoogleConfig): PaymentConfig => {
      const currencyCode = region === 'CA' ? 'CAD' : 'USD';
      const totalAmount = (amount / 100).toString();

      const gatewayMerchantId =
        region === 'CA'
          ? googleConfig.paymentsGatewayMerchantId.ca
          : googleConfig.paymentsGatewayMerchantId.us;

      const networks = (googleConfig.paymentsNetworks ?? []).filter(
        (n: unknown) => typeof n === 'string' && n.length > 0
      );

      return {
        region,
        currencyCode,
        totalAmount,
        gateway: googleConfig.paymentsGateway,
        gatewayMerchantId,
        merchantName: googleConfig.googleMerchantName,
        merchantId: googleConfig.googleMerchantId,
        environment: googleConfig.paymentsEnvironment,
        networks,
      };
    },
    [region]
  );

  const buildTokenizationSpecification = useCallback(
    (paymentConfig: PaymentConfig): google.payments.api.PaymentMethodTokenizationSpecification => {
      if (config.processor === PaymentProcessor.WORLDPAY) {
        const merchantOrderId = config.storeId.slice(0, 20);
        const merchantTransactionId = merchantOrderId;
        const env = Constants?.expoConfig?.extra?.eas?.env;
        const merchantReportGroup = `${env}-FHS-${paymentConfig.region}`.toUpperCase();

        return {
          type: 'PAYMENT_GATEWAY',
          parameters: {
            gateway: paymentConfig.gateway,
            'vantiv:merchantPayPageId': paymentConfig.gatewayMerchantId,
            'vantiv:merchantOrderId': merchantOrderId,
            'vantiv:merchantTransactionId': merchantTransactionId,
            'vantiv:merchantReportGroup': merchantReportGroup,
          },
        };
      }

      return {
        type: 'PAYMENT_GATEWAY',
        parameters: {
          gateway: paymentConfig.gateway,
          gatewayMerchantId: paymentConfig.gatewayMerchantId,
        },
      };
    },
    [config.processor, config.storeId]
  );

  const buildPaymentDataRequest = useCallback(
    (
      paymentConfig: PaymentConfig,
      tokenizationSpecification: google.payments.api.PaymentMethodTokenizationSpecification
    ): google.payments.api.PaymentDataRequest => {
      const countryCode = paymentConfig.region === 'CA' ? 'CA' : 'US';

      const cardPaymentMethod: google.payments.api.PaymentMethodSpecification = {
        type: 'CARD',
        parameters: {
          allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
          allowedCardNetworks: paymentConfig.networks as google.payments.api.CardNetwork[],
          assuranceDetailsRequired: true,
          billingAddressRequired: false,
          billingAddressParameters: {
            format: 'FULL',
            phoneNumberRequired: false,
          },
        },
        tokenizationSpecification,
      };

      return {
        apiVersion: 2,
        apiVersionMinor: 0,
        allowedPaymentMethods: [cardPaymentMethod],
        transactionInfo: {
          totalPriceStatus: 'FINAL',
          totalPrice: paymentConfig.totalAmount,
          currencyCode: paymentConfig.currencyCode,
          countryCode,
        },
        merchantInfo: {
          merchantName: paymentConfig.merchantName || 'Firehouse Subs',
          merchantId: paymentConfig.merchantId ?? '',
        },
      };
    },
    []
  );

  const parsePaymentResponse = useCallback(
    (paymentData: google.payments.api.PaymentData): GooglePayDetails => {
      const { paymentMethodData } = paymentData;
      const token = paymentMethodData.tokenizationData.token;
      const { info, description } = paymentMethodData;

      const billingInfo = info?.billingAddress;
      const streetAddress = [billingInfo?.address1, billingInfo?.address2]
        .filter(Boolean)
        .join(', ');

      const billingAddress = {
        locality: billingInfo?.locality || '',
        postalCode: billingInfo?.postalCode || '',
        region: billingInfo?.administrativeArea || '',
        streetAddress,
      };

      const formattedAddress = [
        streetAddress,
        billingAddress.locality,
        billingAddress.region,
        billingAddress.postalCode,
      ]
        .filter(Boolean)
        .join(', ');

      let signature = '';
      let data = '';
      let version = '';

      if (config.processor === PaymentProcessor.ORBITAL) {
        try {
          const parsedToken = JSON.parse(token);
          signature = parsedToken.signature || '';
          data = parsedToken.signedMessage || '';
          version = parsedToken.protocolVersion || 'ECv2';
        } catch {
          logger.error({
            message: '[GooglePay Reload] Failed to parse Orbital token',
          });
        }
      }

      return {
        signature,
        country: region,
        data,
        formatted: formattedAddress,
        paymentData: token,
        primary: true,
        type: 'work',
        version,
        billingAddress,
        PaymentMethodData: {
          paymentType: info?.cardNetwork?.toLowerCase() || '',
          displayName: description || '',
        },
      };
    },
    [config.processor, region]
  );

  /**
   * Triggers Google Pay payment sheet for the given amount
   * @param amountInCents - Amount to charge in cents
   * @returns GooglePayDetails if successful, null if cancelled/failed
   */
  const requestGooglePayForReload = useCallback(
    async (amountInCents: number): Promise<GooglePayDetails | null> => {
      setIsLoading(true);
      setError(null);

      try {
        if (typeof google === 'undefined' || !google.payments?.api?.PaymentsClient) {
          throw new Error('Google Pay API not available');
        }

        const googleConfig = getConfiguration();
        if (!googleConfig) {
          throw new Error('Google Pay configuration not found');
        }

        const paymentConfig = buildPaymentConfig(amountInCents, googleConfig);

        if (!paymentConfig.gateway || !paymentConfig.gatewayMerchantId) {
          throw new Error('Google Pay gateway configuration missing');
        }

        const tokenizationSpecification = buildTokenizationSpecification(paymentConfig);
        const paymentDataRequest = buildPaymentDataRequest(
          paymentConfig,
          tokenizationSpecification
        );

        const env = paymentConfig.environment === 'test' ? 'TEST' : 'PRODUCTION';
        const clientConfig: google.payments.api.PaymentOptions = {
          environment: env,
        };

        if (paymentConfig.merchantId && paymentConfig.merchantName) {
          clientConfig.merchantInfo = {
            merchantName: paymentConfig.merchantName,
            merchantId: paymentConfig.merchantId,
          };
        }

        const paymentsClient = new google.payments.api.PaymentsClient(clientConfig);

        logger.info({
          message: '[GooglePay Reload] Opening Google Pay sheet',
          amount: amountInCents,
          processor: config.processor,
        });

        const paymentData = await paymentsClient.loadPaymentData(paymentDataRequest);

        if (!paymentData || !paymentData.paymentMethodData) {
          logger.warn({
            message: '[GooglePay Reload] No payment data returned (user cancelled?)',
          });
          return null;
        }

        const googlePayDetails = parsePaymentResponse(paymentData);

        if (config.processor === PaymentProcessor.WORLDPAY) {
          logger.info({
            message: '[GooglePay Reload] Converting token for Worldpay',
            tokenLength: googlePayDetails.paymentData?.length || 0,
          });
          // empty data because reload mutation only excepts token and billing address
          return {
            ...googlePayDetails,
            signature: '',
            data: '',
            version: '',
          };
        }

        return googlePayDetails;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        logger.error({
          message: '[GooglePay Reload] Payment failed',
          error: errorMessage,
        });
        setError(errorMessage);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [
      getConfiguration,
      buildPaymentConfig,
      buildTokenizationSpecification,
      buildPaymentDataRequest,
      parsePaymentResponse,
      config.processor,
    ]
  );

  return {
    requestGooglePayForReload,
    isLoading,
    error,
  };
}
